class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      initCartDrawerUpsell(document.getElementById('CartDrawer') || this);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function initCartDrawerUpsell(scope = document) {
  scope.querySelectorAll('[data-upsell-variant]').forEach((select) => {
    if (select.dataset.upsellBound === 'true') return;
    select.dataset.upsellBound = 'true';

    select.addEventListener('change', () => {
      const item = select.closest('.cart-drawer-upsell__item');
      const button = item?.querySelector('[data-upsell-add]');
      const selectedOption = select.options[select.selectedIndex];
      if (!button || !selectedOption) return;

      button.dataset.variantId = select.value;
      button.disabled = selectedOption.disabled;
    });
  });

  scope.querySelectorAll('[data-upsell-add]').forEach((button) => {
    if (button.dataset.upsellBound === 'true') return;
    button.dataset.upsellBound = 'true';

    button.addEventListener('click', async () => {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      const textEl = button.querySelector('[data-upsell-add-text]') || button;
      const originalText = textEl.textContent.trim();

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      textEl.textContent = 'Adding...';

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer
          ? cartDrawer.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble'];

        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const parsedState = await response.json();
        if (parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'Unable to add product');
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && parsedState.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(parsedState);
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error(error);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        textEl.textContent = originalText;
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCartDrawerUpsell(document);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) initCartDrawerUpsell(cartDrawer);
      }, 50);
    });
  }
});
